import { Button, Container, Typography } from '@cleartrip/ct-design-components'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'

import LazyLoopingVideo from '@/components/srp/LazyLoopingVideo'
import {
    getPeUpgradeCalloutCopy,
    INDIGO_UPFRONT_NUDGE_THEME,
    PE_CALLOUT_SEAM_OVERLAP_PX,
    PE_INTRO_TRANSITION_MS,
    PE_UPGRADE_CALLOUT_AMOUNT,
    PE_UPGRADE_CALLOUT_HEIGHT_PX,
    PE_UPGRADE_CALLOUT_WIDTH_PX,
    PE_UPGRADE_MEDIA_LEFT_MASK,
    PE_UPGRADE_MEDIA_LEFT_UNDERLAY,
    PE_UPGRADE_MEDIA_LEFT_UNDERLAY_BLUR_PX,
    PE_UPGRADE_MEDIA_LEFT_UNDERLAY_WIDTH_PX,
    PE_UPGRADE_MEDIA_TOP_MASK,
    UPGRADE_NUDGE_VARIANT,
    UpgradeNudgeVariant,
} from '@/constants/flights/premiumEconomy'
import { formatCurrency } from '@/utils'

import styles from './PEUpgradeCalloutCard.module.scss'

type PEUpgradeCalloutCardProps = {
    upgradeAmount?: number
    onUpgrade: () => void
    isVisible: boolean
    showVideoModal: () => void
    /** Muted, looping cabin video that fades in over the seat image. */
    videoSrc?: string
    title?: string | null
    imageSrc?: string | null
    variant?: UpgradeNudgeVariant
    /**
     * Height of the fare card this callout is attached to. Required so the
     * callout does not stretch to taller siblings in the fare strip.
     */
    heightPx?: number
}

const UPGRADE = 'Upgrade'

const PE_CALLOUT_IMAGE = {
    left: -39,
    width: 340,
    heightRatio: 226 / PE_UPGRADE_CALLOUT_HEIGHT_PX,
}
// Figma 4094:43563 — gold soft-edge that blends the media top into the banner fill.
const PE_CALLOUT_BLUR = {
    topRatio: 158 / PE_UPGRADE_CALLOUT_HEIGHT_PX,
    width: 254,
    height: 55,
    blurPx: 14.95,
}
const PE_CALLOUT_CONTENT = { left: 77, top: 16, width: 121 }

const PEUpgradeCalloutCard: React.FC<PEUpgradeCalloutCardProps> = ({
    upgradeAmount = PE_UPGRADE_CALLOUT_AMOUNT,
    onUpgrade,
    isVisible,
    showVideoModal,
    videoSrc,
    heightPx,
    title,
    imageSrc,
    variant = UPGRADE_NUDGE_VARIANT.PREMIUM,
}) => {
    const [isAnimated, setIsAnimated] = useState(false)

    useEffect(() => {
        if (!isVisible) {
            setIsAnimated(false)
            return
        }
        const rafId = requestAnimationFrame(() => setIsAnimated(true))
        return () => cancelAnimationFrame(rafId)
    }, [isVisible])

    if (!isVisible) return null
    const resolvedHeightPx = heightPx || PE_UPGRADE_CALLOUT_HEIGHT_PX

    const isIndigo = variant === UPGRADE_NUDGE_VARIANT.INDIGO
    const upgradeCalloutCopy = title
        ? title.replace('{amount}', upgradeAmount.toLocaleString('en-IN'))
        : isIndigo
          ? ''
          : getPeUpgradeCalloutCopy(formatCurrency(upgradeAmount))
    if (!upgradeCalloutCopy) return null

    // IU: only BE upgradeCallout media. PE: parent still passes 7439 seat + mp4.
    const bannerGradient = isIndigo
        ? INDIGO_UPFRONT_NUDGE_THEME.bannerGradient
        : 'linear-gradient(93.56deg, #E6BF83 7.13%, #FAF5EB 46.03%)'
    const scale = resolvedHeightPx / PE_UPGRADE_CALLOUT_HEIGHT_PX
    const mediaLeftPx = isIndigo ? INDIGO_UPFRONT_NUDGE_THEME.bannerImageLeftPx : PE_CALLOUT_IMAGE.left
    const mediaTopPx = isIndigo ? INDIGO_UPFRONT_NUDGE_THEME.bannerImageTopPx * scale : undefined
    const mediaWidthPx = isIndigo
        ? INDIGO_UPFRONT_NUDGE_THEME.bannerImageWidthPx
        : PE_CALLOUT_IMAGE.width
    const mediaHeight = isIndigo
        ? `${INDIGO_UPFRONT_NUDGE_THEME.bannerImageHeightPx * scale}px`
        : `${PE_CALLOUT_IMAGE.heightRatio * 100}%`
    const blendTop = isIndigo
        ? `${INDIGO_UPFRONT_NUDGE_THEME.bannerBlendTopPx * scale}px`
        : `${PE_CALLOUT_BLUR.topRatio * 100}%`
    const blendWidthPx = isIndigo ? INDIGO_UPFRONT_NUDGE_THEME.bannerBlendWidthPx : PE_CALLOUT_BLUR.width
    const blendHeightPx = isIndigo
        ? INDIGO_UPFRONT_NUDGE_THEME.bannerBlendHeightPx
        : PE_CALLOUT_BLUR.height
    const blendColor = isIndigo
        ? INDIGO_UPFRONT_NUDGE_THEME.bannerBlend
        : 'linear-gradient(118.95deg, #E6BF83 7.13%, #FAF5EB 46.03%)'
    const blendBlurPx = isIndigo ? INDIGO_UPFRONT_NUDGE_THEME.bannerBlendBlurPx : PE_CALLOUT_BLUR.blurPx

    return (
        <Container
            className="flex-shrink-0 p-relative z-0 o-visible"
            style={{
                // Same negative-margin overlap as PE — tucks under the fare card's right
                // rounded corners to create the curve. IU uses identical layout, only colors differ.
                marginLeft: `-${PE_CALLOUT_SEAM_OVERLAP_PX}px`,
                width: `${PE_UPGRADE_CALLOUT_WIDTH_PX}px`,
                height: `${resolvedHeightPx}px`,
                // Lock to the attached card's measured height — never stretch to a
                // taller sibling in the fare strip (that also misplaces the gold
                // top-of-video blend, which is positioned as a % of this height).
                alignSelf: 'flex-start',
            }}
        >
            <Container
                className={`p-relative h-100p br-16 o-hidden ${styles.callout} ${
                    isAnimated ? styles.calloutVisible : ''
                }`}
                css={{
                    width: `${PE_UPGRADE_CALLOUT_WIDTH_PX}px`,
                    background: bannerGradient,
                    // Keep expand duration in lockstep with the nudge fade-out.
                    ['--pe-callout-expand-ms' as string]: `${PE_INTRO_TRANSITION_MS}ms`,
                }}
            >
                <Container
                    className={`p-absolute o-hidden disable-pointer-events ${isIndigo ? '' : 'b-0'}`}
                    css={{
                        left: `${mediaLeftPx}px`,
                        top: mediaTopPx,
                        width: `${mediaWidthPx}px`,
                        height: mediaHeight,
                        // PE dissolves the photo top into gold. IU is Figma 4709:46309 —
                        // hard photo rect + #F5F7FC bloom, no mask.
                        ...(!isIndigo
                            ? {
                                  WebkitMaskImage: PE_UPGRADE_MEDIA_TOP_MASK,
                                  maskImage: PE_UPGRADE_MEDIA_TOP_MASK,
                              }
                            : {}),
                    }}
                >
                    {!isIndigo && (
                        <Container
                            aria-hidden
                            className="p-absolute t-0 b-0 l-0 disable-pointer-events"
                            css={{
                                width: `${PE_UPGRADE_MEDIA_LEFT_UNDERLAY_WIDTH_PX + PE_UPGRADE_MEDIA_LEFT_UNDERLAY_BLUR_PX}px`,
                                background: PE_UPGRADE_MEDIA_LEFT_UNDERLAY,
                                filter: `blur(${PE_UPGRADE_MEDIA_LEFT_UNDERLAY_BLUR_PX}px)`,
                            }}
                        />
                    )}
                    <Container
                        className="p-absolute"
                        css={{
                            inset: 0,
                            ...(!isIndigo
                                ? {
                                      WebkitMaskImage: PE_UPGRADE_MEDIA_LEFT_MASK,
                                      maskImage: PE_UPGRADE_MEDIA_LEFT_MASK,
                                  }
                                : {}),
                        }}
                    >
                        {imageSrc && (
                            <Image
                                fill
                                unoptimized
                                alt=""
                                height={0}
                                sizes="340px"
                                src={imageSrc}
                                style={{ objectFit: 'cover' }}
                                width={0}
                            />
                        )}
                        {/* Muted looping cabin video, lazily mounted after first paint; fades
                            in over the seat image above (which stays as the loading fallback). */}
                        {videoSrc && (
                            <LazyLoopingVideo
                                src={videoSrc}
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    width: '100%',
                                    height: '100%',
                                }}
                            />
                        )}
                    </Container>
                </Container>
                <Container
                    aria-hidden
                    className="p-absolute l-0 br-12 disable-pointer-events"
                    css={{
                        top: blendTop,
                        width: `${blendWidthPx}px`,
                        height: `${blendHeightPx}px`,
                        zIndex: 1,
                        filter: `blur(${blendBlurPx}px)`,
                        background: blendColor,
                    }}
                />
                <Container
                    className={`flex flex-column flex-gap-4 p-absolute z-1 ${styles.content} ${
                        isAnimated ? styles.contentVisible : ''
                    }`}
                    css={{
                        left: `${PE_CALLOUT_CONTENT.left}px`,
                        top: `${PE_CALLOUT_CONTENT.top}px`,
                        width: `${PE_CALLOUT_CONTENT.width}px`,
                    }}
                >
                    <Typography
                        colorCode="#1A1A1A"
                        css={{ width: '100%', wordBreak: 'break-word' }}
                        variant="HM3"
                    >
                        {upgradeCalloutCopy}
                    </Typography>
                    <Button isFullWidth color="primary" size="small" variant="contained" onClick={onUpgrade}>
                        {UPGRADE}
                    </Button>
                </Container>
                {/* Invisible hit area over the media half — opens cabin video drawer. */}
                <Container
                    aria-label="Open cabin media"
                    className="p-absolute l-0 b-0 c-pointer"
                    css={{
                        width: '100%',
                        height: '50%',
                        zIndex: 2,
                        background: 'transparent',
                    }}
                    role="button"
                    tabIndex={0}
                    onClick={showVideoModal}
                    onKeyDown={(event: React.KeyboardEvent) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            showVideoModal()
                        }
                    }}
                />
            </Container>
        </Container>
    )
}

export default PEUpgradeCalloutCard
